/**
 * Shared Vimeo / YouTube embed helpers (Academy + PageVideoHelp).
 */

export const normalizeEmbedSrc = (raw: string): string => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    const iframeSrcMatch = trimmed.match(/src=["']([^"']+)["']/i);
    if (iframeSrcMatch?.[1]) return iframeSrcMatch[1].trim();
    return trimmed;
};

export const extractYoutubeId = (url: string): string | null => {
    const match = url.match(
        /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/
    );
    return match?.[1] || null;
};

export const extractVimeoId = (url: string): string | null => {
    if (!url) return null;
    const playerMatch = url.match(/player\.vimeo\.com\/video\/(\d+)/);
    if (playerMatch?.[1]) return playerMatch[1];
    const vimeoMatch = url.match(
        /vimeo\.com\/(?:channels\/[^/?#]+\/|groups\/[^/?#]+\/videos\/|album\/\d+\/video\/|video\/|)(\d+)/
    );
    if (vimeoMatch?.[1]) return vimeoMatch[1];
    return null;
};

/** Private/unlisted Vimeo-Links: /VIDEO_ID/HASH oder ?h=HASH */
export const extractVimeoPrivacyHash = (url: string): string | null => {
    if (!url) return null;
    try {
        const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
        const hParam = parsed.searchParams.get('h');
        if (hParam && /^[a-f0-9]+$/i.test(hParam)) return hParam;
    } catch {
        /* ignore */
    }
    const pathHash = url.match(/vimeo\.com\/(?:video\/)?(\d+)\/([a-f0-9]+)(?:[/?#]|$)/i);
    if (pathHash?.[2]) return pathHash[2];
    return null;
};

export const buildVimeoPublicUrl = (vimeoId: string, privacyHash?: string | null): string =>
    privacyHash ? `https://vimeo.com/${vimeoId}/${privacyHash}` : `https://vimeo.com/${vimeoId}`;

export const buildVimeoPlayerUrl = (
    vimeoId: string,
    privacyHash?: string | null,
    extraParams = ''
): string => {
    const params = new URLSearchParams();
    if (privacyHash) params.set('h', privacyHash);
    if (extraParams) {
        const extra = new URLSearchParams(extraParams.replace(/^\?/, ''));
        extra.forEach((value, key) => params.set(key, value));
    }
    const qs = params.toString();
    return `https://player.vimeo.com/video/${vimeoId}${qs ? `?${qs}` : ''}`;
};

export const isImageMediaUrl = (url: string): boolean => {
    if (!url?.trim()) return false;
    const lower = url.toLowerCase();
    if (/\.(png|jpe?g|gif|webp|svg|bmp)(\?|#|$)/i.test(lower)) return true;
    if (lower.includes('imgur.com') || lower.includes('i.imgur.com')) return true;
    if (lower.includes('vimeocdn.com') || lower.includes('i.vimeocdn.com')) return true;
    if (lower.includes('firebasestorage.googleapis.com') || lower.includes('firebasestorage.app')) {
        return true;
    }
    return false;
};

export const isVideoEmbedUrl = (url: string): boolean => {
    return Boolean(extractYoutubeId(url) || extractVimeoId(url));
};

export const getEmbedUrl = (url: string): string => {
    const normalized = normalizeEmbedSrc(url);
    if (!normalized) return '';
    const youtubeId = extractYoutubeId(normalized);
    if (youtubeId) return `https://www.youtube.com/embed/${youtubeId}`;
    const vimeoId = extractVimeoId(normalized);
    if (vimeoId) {
        return buildVimeoPlayerUrl(vimeoId, extractVimeoPrivacyHash(normalized));
    }
    return normalized;
};

/**
 * Sofort-Poster ohne CORS — identisch zu PEBase academy.html.
 * Videos haben oft kein thumbnail-Feld; oEmbed ist unzuverlässig.
 */
export const getVimeoPosterUrl = (url: string): string | null => {
    const vimeoId = extractVimeoId(url);
    return vimeoId ? `https://vumbnail.com/${vimeoId}.jpg` : null;
};

/** Optional: oEmbed-Upgrade (wie PEBase hydratePosters). Bei Fehler bleibt vumbnail. */
export const fetchVimeoPosterUrl = async (embedSrc: string): Promise<string | null> => {
    const vimeoId = extractVimeoId(embedSrc);
    if (!vimeoId) return null;
    const privacyHash = extractVimeoPrivacyHash(embedSrc);
    const candidates = [
        buildVimeoPublicUrl(vimeoId, privacyHash),
        ...(privacyHash ? [`https://vimeo.com/${vimeoId}`] : []),
    ];
    for (const candidate of candidates) {
        try {
            const response = await fetch(
                `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(candidate)}`
            );
            if (!response.ok) continue;
            const data = await response.json();
            if (typeof data?.thumbnail_url === 'string' && data.thumbnail_url.trim()) {
                return data.thumbnail_url.replace(/^http:\/\//i, 'https://');
            }
        } catch {
            /* CORS / Netzwerk — Fallback bleibt vumbnail */
        }
    }
    return null;
};

export const getYoutubePosterUrl = (url: string): string | null => {
    const youtubeId = extractYoutubeId(url);
    return youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : null;
};

export const buildAutoplayEmbedUrl = (embedSrc: string): string => {
    const vimeoId = extractVimeoId(embedSrc);
    if (vimeoId) {
        return buildVimeoPlayerUrl(
            vimeoId,
            extractVimeoPrivacyHash(embedSrc),
            'autoplay=1&title=0&byline=0&portrait=0'
        );
    }
    const base = getEmbedUrl(embedSrc);
    if (!base) return '';
    return `${base}${base.includes('?') ? '&' : '?'}autoplay=1`;
};

export const requestElementFullscreen = async (node: HTMLElement) => {
    if (node.requestFullscreen) {
        await node.requestFullscreen();
        return;
    }
    const legacyNode = node as HTMLElement & {
        webkitRequestFullscreen?: () => Promise<void> | void;
        msRequestFullscreen?: () => Promise<void> | void;
    };
    if (legacyNode.webkitRequestFullscreen) {
        await legacyNode.webkitRequestFullscreen();
        return;
    }
    if (legacyNode.msRequestFullscreen) {
        await legacyNode.msRequestFullscreen();
    }
};

export const exitElementFullscreen = async () => {
    if (document.exitFullscreen) {
        await document.exitFullscreen();
        return;
    }
    const legacyDocument = document as Document & {
        webkitExitFullscreen?: () => Promise<void> | void;
        msExitFullscreen?: () => Promise<void> | void;
    };
    if (legacyDocument.webkitExitFullscreen) {
        await legacyDocument.webkitExitFullscreen();
        return;
    }
    if (legacyDocument.msExitFullscreen) {
        await legacyDocument.msExitFullscreen();
    }
};
